import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:3041';

const inputCls =
  'w-full px-3.5 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700/60 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition placeholder-gray-400';

const roleLabel = {
  SUPERADMIN: 'Super Admin',
  AVUK: 'Avuk',
  KRYEINSPEKTORI: 'Kryeinspektori',
  INSPEKTORI: 'Inspektori',
  KOMPANIA: 'Kompania',
};

export default function Settings() {
  const { language } = useLanguage();
  const { user } = useAuth();

  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [savingPw, setSavingPw] = useState(false);
  const [simulating, setSimulating] = useState(false);
  const [isDark, setIsDark] = useState(document.documentElement.classList.contains('dark'));

  const authHeader = { Authorization: `Bearer ${localStorage.getItem('access_token')}` };

  // sync isDark when theme changes via F8 or header button
  useEffect(() => {
    const obs = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains('dark'));
    });
    obs.observe(document.documentElement, { attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);

  const toggleTheme = () => {
    const next = isDark ? 'light' : 'dark';
    localStorage.setItem('dio-theme', next);
    document.documentElement.classList.toggle('dark', next === 'dark');
    setIsDark(next === 'dark');
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwForm.new_password !== pwForm.confirm_password) {
      toast.error('Fjalëkalimet e reja nuk përputhen');
      return;
    }
    if (pwForm.new_password.length < 6) {
      toast.error('Fjalëkalimi duhet të ketë së paku 6 karaktere');
      return;
    }
    setSavingPw(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/auth/change-password`,
        { current_password: pwForm.current_password, new_password: pwForm.new_password },
        { headers: authHeader }
      );
      toast.success('Fjalëkalimi u ndryshua me sukses');
      setPwForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Gabim gjatë ndryshimit të fjalëkalimit');
    } finally {
      setSavingPw(false);
    }
  };

  const handleSimulate = async () => {
    setSimulating(true);
    try {
      await axios.post(`${BACKEND_URL}/api/simulate/generate`, {}, { headers: authHeader });
      toast.success('Të dhënat simuluese u gjeneruan (24h)');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Gabim gjatë simulimit');
    } finally {
      setSimulating(false);
    }
  };

  return (
    <Layout>
      <div className="h-full p-5 flex flex-col gap-4 overflow-hidden">

        {/* ── Titulli ── */}
        <div className="flex items-center gap-3 flex-shrink-0">
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center shadow-md">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">Cilësimet</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">Menaxho llogarinë dhe preferencat</p>
          </div>
        </div>

        {/* ── Grid ── */}
        <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4 min-h-0">

          {/* Fjalëkalimi */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 flex flex-col overflow-hidden">
            <div className="px-5 py-3.5 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2.5 bg-gradient-to-r from-blue-50 to-transparent dark:from-blue-900/20 dark:to-transparent">
              <div className="w-7 h-7 rounded-lg bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center">
                <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Ndrysho Fjalëkalimin</h2>
            </div>
            <form onSubmit={handleChangePassword} className="p-5 flex flex-col gap-4 flex-1">
              {[
                { label: 'Fjalëkalimi aktual', key: 'current_password', auto: 'current-password' },
                { label: 'Fjalëkalimi i ri', key: 'new_password', auto: 'new-password' },
                { label: 'Konfirmo fjalëkalimin e ri', key: 'confirm_password', auto: 'new-password' },
              ].map(({ label, key, auto }) => (
                <div key={key}>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">{label}</label>
                  <input
                    type="password"
                    value={pwForm[key]}
                    onChange={e => setPwForm(p => ({ ...p, [key]: e.target.value }))}
                    className={inputCls}
                    required
                    autoComplete={auto}
                    minLength={key === 'new_password' ? 6 : undefined}
                  />
                </div>
              ))}
              <div className="flex justify-end mt-auto pt-1">
                <button
                  type="submit"
                  disabled={savingPw}
                  className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-semibold rounded-xl transition shadow-sm"
                >
                  {savingPw ? 'Duke ndryshuar...' : 'Ruaj Fjalëkalimin'}
                </button>
              </div>
            </form>
          </div>

          {/* Kolona djathtas */}
          <div className="flex flex-col gap-4 min-h-0">

            {/* Info + Tema */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden flex-1">
              <div className="px-5 py-3.5 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2.5 bg-gradient-to-r from-gray-50 to-transparent dark:from-gray-700/30 dark:to-transparent">
                <div className="w-7 h-7 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                  <svg className="w-4 h-4 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Llogaria & Aplikacioni</h2>
              </div>
              <div className="px-5 py-4 space-y-2.5">
                {[
                  { l: 'Aplikacioni', v: 'DIO IoT Data Logger' },
                  { l: 'Versioni', v: '1.0.0' },
                  { l: 'Përdoruesi', v: user?.username || '–' },
                  { l: 'Roli', v: roleLabel[user?.role] || user?.role || '–' },
                  { l: 'Gjuha', v: language === 'sq' ? 'Shqip 🇽🇰' : 'English' },
                ].map(({ l, v }) => (
                  <div key={l} className="flex items-center justify-between">
                    <span className="text-sm text-gray-500 dark:text-gray-400">{l}</span>
                    <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">{v}</span>
                  </div>
                ))}

                {/* Divider */}
                <div className="border-t border-gray-100 dark:border-gray-700 pt-3 mt-1">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Tema vizuale</p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        Tastierë: <kbd className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-xs font-mono border border-gray-200 dark:border-gray-600">F8</kbd>
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={toggleTheme}
                      title="Ndrysho temën (F8)"
                      className={`relative inline-flex h-7 w-13 items-center rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-300 ${isDark ? 'bg-blue-600' : 'bg-gray-300'
                        }`}
                      style={{ width: '52px' }}
                    >
                      <span className={`inline-flex h-5 w-5 rounded-full bg-white shadow-md transform transition-transform duration-300 items-center justify-center ${isDark ? 'translate-x-6' : 'translate-x-1'
                        }`}>
                        {isDark
                          ? <svg className="w-3 h-3 text-blue-600" fill="currentColor" viewBox="0 0 20 20"><path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" /></svg>
                          : <svg className="w-3 h-3 text-yellow-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" /></svg>
                        }
                      </span>
                    </button>
                  </div>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1.5">
                    {isDark ? '🌙 Dark Mode i aktivizuar' : '☀️ Light Mode i aktivizuar'}
                  </p>
                </div>
              </div>
            </div>

            {/* Simulimi — vetëm SUPERADMIN */}
            {/* {user?.role === 'SUPERADMIN' && (
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
                <div className="px-5 py-3.5 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2.5 bg-gradient-to-r from-purple-50 to-transparent dark:from-purple-900/20 dark:to-transparent">
                  <div className="w-7 h-7 rounded-lg bg-purple-100 dark:bg-purple-900/40 flex items-center justify-center">
                    <svg className="w-4 h-4 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Simulimi i të Dhënave</h2>
                    <p className="text-xs text-gray-400 dark:text-gray-500">Gjenero 24h të dhëna fiktive</p>
                  </div>
                </div>
                <div className="px-5 py-4">
                  <button
                    onClick={handleSimulate}
                    disabled={simulating}
                    className="w-full px-4 py-2.5 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-sm font-semibold rounded-xl transition shadow-sm"
                  >
                    {simulating ? '⏳ Duke gjeneruar...' : '⚗️ Gjenero Të Dhëna Simuluese (24h)'}
                  </button>
                </div>
              </div>
            )} */}
          </div>
        </div>
      </div>
    </Layout>
  );
}
